import { Controller } from "@hotwired/stimulus"

const CHECK_SVG = `<svg class="w-3 h-3 text-white" viewBox="0 0 16 16" fill="none"><path d="M3.5 8.5l3 3 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`

export default class extends Controller {
  static targets = ["dropdown", "trigger", "spamDialog", "muteAuthorCheck", "muteAuthorToggle"]
  static values = {
    threadId: String,
    status: String,
    authorId: String,
    authorHandle: String,
    authorMuteUrl: String,
    context: { type: String, default: "feed" }
  }

  #muteAuthor = false
  #muteDomains = new Set()

  toggleDropdown(event) {
    event.stopPropagation()
    const hidden = this.dropdownTarget.classList.contains("hidden")
    this.dropdownTarget.classList.toggle("hidden", !hidden)
    this.#setCardOnTop(hidden)
  }

  hideDropdown(event) {
    if (this.element.contains(event.target)) return
    this.dropdownTarget.classList.add("hidden")
    this.#setCardOnTop(false)
  }

  markDone(event) {
    event.stopPropagation()
    this.applyStatus("done")
  }

  selectStatus(event) {
    const status = event.params.status
    this.dropdownTarget.classList.add("hidden")
    this.#setCardOnTop(false)
    if (status === "spam") {
      this.#muteAuthor = false
      this.#muteDomains = new Set()
      this.#updateMuteAuthorUI()
      this.#resetDomainChecks()
      this.spamDialogTarget.classList.remove("hidden")
      return
    }
    this.applyStatus(status)
  }

  toggleMuteAuthor() {
    this.#muteAuthor = !this.#muteAuthor
    this.#updateMuteAuthorUI()
  }

  toggleDomain(event) {
    const row = event.currentTarget
    const name = row.dataset.domainName
    const check = row.querySelector(".js-domain-check")
    if (this.#muteDomains.has(name)) {
      this.#muteDomains.delete(name)
      this.#uncheckEl(check)
    } else {
      this.#muteDomains.add(name)
      this.#checkEl(check)
    }
  }

  cancelSpam() {
    this.spamDialogTarget.classList.add("hidden")
    this.#muteAuthor = false
    this.#muteDomains = new Set()
    this.#updateMuteAuthorUI()
    this.#resetDomainChecks()
  }

  async confirmSpam() {
    this.spamDialogTarget.classList.add("hidden")
    const token = document.querySelector("meta[name=csrf-token]").content
    const headers = {
      "X-CSRF-Token": token,
      "Content-Type": "application/json",
      "Accept": "text/vnd.turbo-stream.html"
    }
    if (this.#muteAuthor && this.authorMuteUrlValue) {
      await fetch(this.authorMuteUrlValue, { method: "POST", headers })
    }
    for (const name of this.#muteDomains) {
      await fetch("/domains/mutes", { method: "POST", headers, body: JSON.stringify({ name }) })
    }
    this.applyStatus("spam")
  }

  #updateMuteAuthorUI() {
    if (!this.hasMuteAuthorCheckTarget) return
    if (this.#muteAuthor) {
      this.#checkEl(this.muteAuthorCheckTarget)
    } else {
      this.#uncheckEl(this.muteAuthorCheckTarget)
    }
  }

  #resetDomainChecks() {
    this.spamDialogTarget.querySelectorAll(".js-domain-check").forEach(el => this.#uncheckEl(el))
  }

  #checkEl(el) {
    if (!el) return
    el.classList.replace("border-line-strong", "border-brand")
    el.classList.replace("bg-white", "bg-brand")
    el.innerHTML = CHECK_SVG
  }

  #uncheckEl(el) {
    if (!el) return
    el.classList.replace("border-brand", "border-line-strong")
    el.classList.replace("bg-brand", "bg-white")
    el.innerHTML = ""
  }

  // Neither the card's own bookmark/quick-action icons (a sibling layer with
  // an explicit z-index) nor the next card in the feed (a later DOM sibling)
  // otherwise respect the dropdown's own z-index, since none of the card
  // wrappers between it and those siblings carry a z-index of their own.
  #setCardOnTop(open) {
    this.element.closest(".thread-card")?.classList.toggle("z-30", open)
    this.element.closest("[data-thread-id]")?.classList.toggle("z-30", open)
  }

  // The single entry point for a status change. A thread that no longer belongs
  // in the current view (Done/Ignore/Spam in the inbox, or anything off the
  // active status filter) slides out and collapses before the server removes it;
  // one that stays (New/Waiting) is re-rendered in place.
  applyStatus(status) {
    const from = this.statusValue
    const willLeave = this.#willLeaveView(status)

    this.statusValue = status
    const card = this.element.closest("[data-thread-id]")

    // Mark the card as leaving before dispatching so listeners (e.g. Review
    // mode) keep it on screen through its slide-out instead of hiding it.
    if (willLeave && card) this.#animateOut(card, () => this.#submit(status))
    else this.#submit(status)

    this.dispatch("changed", { detail: { threadId: this.threadIdValue, from, to: status, willLeave }, bubbles: true })
  }

  // Mirrors Threads::TriagesController#card_stream's still_visible rule: the bare
  // inbox (no source/watching/sentiment/since/query axis engaged) restricts to
  // New + Waiting, an explicit status filter requires an exact match, and any
  // other filter axis leaves every status visible. Guessing this wrong makes the
  // card play a leave animation the server then reverses with a replace.
  #willLeaveView(status) {
    const params = new URLSearchParams(window.location.search)
    const filterStatus = params.get("status")
    if (filterStatus) return status !== filterStatus

    const restrictsToDefaultStatuses = !params.has("sources[]") && !params.has("watching[]") &&
      !params.has("sentiment[]") && (params.get("since") ?? "all") === "all" && !params.get("q")
    return restrictsToDefaultStatuses && !DEFAULT_STATUSES.includes(status)
  }

  #animateOut(card, done) {
    card.style.maxHeight = `${card.offsetHeight}px`
    card.classList.add("thread-leaving")
    // Start collapsing while the slide-out is still finishing so the two read as
    // one motion, then submit once the .32s collapse has settled.
    setTimeout(() => card.classList.add("thread-collapsing"), 160)
    setTimeout(done, 500)
  }

  // filter_params carries the feed's active filter (since/alert/platform/q/status)
  // as a raw query string, so the server can keep the progress bar scoped to
  // whatever the user is currently filtered to instead of the whole inbox.
  async #submit(status) {
    const token = document.querySelector("meta[name=csrf-token]").content
    const response = await fetch(`/threads/${this.threadIdValue}/triage`, {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": token,
        "Content-Type": "application/json",
        "Accept": "text/vnd.turbo-stream.html"
      },
      body: JSON.stringify({ status, context: this.contextValue, filter_params: window.location.search.replace(/^\?/, "") })
    })
    if (response.ok) {
      const html = await response.text()
      Turbo.renderStreamMessage(html)
    }
  }
}

const DEFAULT_STATUSES = ["new", "waiting"]
